<template>
    <div class="relative inline-block w-80">
        <!-- Trigger -->
        <button @click="toggleDropdown"
            class="w-full border rounded-lg px-4 py-2 flex justify-start items-center bg-white">
            <svg class="w-6 h-6 text-gray-500 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                <path fill-rule="evenodd"
                    d="M5 5a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1 2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2ZM3 19v-7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Zm6.01-6a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-10 4a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z"
                    clip-rule="evenodd" />
            </svg>

            <span class="text-gray-500 text-left ml-2">
                {{ displayLabel }}
            </span>
        </button>

        <!-- Dropdown -->
        <div v-if="showDropdown" class="absolute bg-white border rounded mt-2 shadow-lg z-10 w-full p-4">
            <!-- Header -->
            <div class="flex justify-between items-center mb-2">
                <button @click="prevYear">←</button>
                <span class="font-semibold">{{ currentYear }}</span>
                <button @click="nextYear">→</button>
            </div>

            <!-- Month Grid -->
            <div class="grid grid-cols-3 gap-2 text-center mb-4">
                <button v-for="(month, index) in months" :key="index" @click="selectMonth(index + 1)"
                    :class="monthClass(index + 1)">
                    {{ month }}
                </button>
            </div>

            <!-- Footer -->
            <div class="flex justify-between">
                <button @click="selectThisMonth" class="bg-blue-600 text-white px-3 py-2 rounded w-[48%]">
                    This Month
                </button>
                <button @click="clear" class="border px-3 py-2 rounded w-[48%]">
                    Clear
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "PeriodDate",
    data() {
        const now = new Date();
        return {
            showDropdown: false,
            currentYear: now.getFullYear(),
            selectedStart: null,
            selectedEnd: null,
            months: [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
            ]
        };
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    computed: {
        displayLabel() {
            const format = (m) => `${this.months[m.month - 1].slice(0, 3)} ${m.year}`;
            if (this.selectedStart && this.selectedEnd) {
                return `${format(this.selectedStart)} - ${format(this.selectedEnd)}`;
            } else if (this.selectedStart) {
                return `${format(this.selectedStart)}`;
            }
            return "Select Period Date";
        }
    },
    methods: {
        toggleDropdown() {
            this.showDropdown = !this.showDropdown;
        },
        prevYear() {
            this.currentYear--;
        },
        nextYear() {
            this.currentYear++;
        },
        getEndOfMonth({ year, month }) {
            const lastDay = new Date(year, month, 0).getDate();
            return `${year}-${String(month).padStart(2, "0")}-${lastDay}`;
        },
        handleClickOutside(event) {
            const dropdown = this.$el;
            if (this.showDropdown && !dropdown.contains(event.target)) {
                this.showDropdown = false;
            }
        },
        selectMonth(month) {
            const clicked = { year: this.currentYear, month };
            if (
                this.selectedStart &&
                !this.selectedEnd &&
                this.selectedStart.year === clicked.year &&
                this.selectedStart.month === clicked.month
            ) {
                return;
            }

            if (!this.selectedStart || (this.selectedStart && this.selectedEnd)) {
                this.selectedStart = clicked;
                this.selectedEnd = null;

                this.$emit("month-selected", {
                    selected: this.getEndOfMonth(this.selectedStart),
                });
            } else if (this.selectedStart && !this.selectedEnd) {
                const startDate = new Date(this.selectedStart.year, this.selectedStart.month - 1);
                const endDate = new Date(clicked.year, clicked.month - 1);

                if (endDate >= startDate) {
                    this.selectedEnd = clicked;

                    this.$emit("range-selected", {
                        startRange: this.getEndOfMonth(this.selectedStart),
                        endRange: this.getEndOfMonth(this.selectedEnd),
                    });
                } else {
                    this.selectedStart = clicked;
                    this.selectedEnd = null;

                    this.$emit("month-selected", {
                        selected: this.getEndOfMonth(this.selectedStart),
                    });
                }
            }
        },
        selectThisMonth() {
            const now = new Date();
            this.selectedStart = {
                year: now.getFullYear(),
                month: now.getMonth() + 1
            };
            this.selectedEnd = null;
            this.showDropdown = false;

            this.$emit("month-selected", {
                selected: this.getEndOfMonth(this.selectedStart)
            });
        },
        clear() {
            this.selectedStart = null;
            this.selectedEnd = null;
            this.showDropdown = false;
            this.$emit("clear");

        },
        monthClass(month) {
            const isSelected =
                (this.selectedStart &&
                    this.selectedStart.month === month &&
                    this.selectedStart.year === this.currentYear) ||
                (this.selectedEnd &&
                    this.selectedEnd.month === month &&
                    this.selectedEnd.year === this.currentYear);

            const isInRange =
                this.selectedStart &&
                this.selectedEnd &&
                this.isMonthInRange(month);

            return {
                'bg-blue-600 text-white': isSelected,
                'bg-blue-100 text-black': isInRange && !isSelected,
                'hover:bg-blue-50': true,
                'py-2 rounded font-medium': true
            };
        },
        isMonthInRange(month) {
            const start = new Date(this.selectedStart.year, this.selectedStart.month - 1);
            const end = new Date(this.selectedEnd.year, this.selectedEnd.month - 1);
            const current = new Date(this.currentYear, month - 1);
            return current > start && current < end;
        }
    }
};
</script>

<style scoped>
button {
    transition: all 0.2s;
}
</style>